import { useField } from 'formik';
import React__default from 'react';
import ReactAutosizeTextArea from 'react-textarea-autosize';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { setDisabledDataAttribute, setReadOnlyDataAttribute } from '../inputs/InputCommon.js';
import { Stack } from '../layout/Stack.js';
import { getThemeVariantColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CSSDefaultInputField } from './common.js';
import { useTextFieldWrappers, hasError } from './Field.js';
import { setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

/**
 * styled components
 */ const StyledReactAutosizeTextArea = styled(ReactAutosizeTextArea)`
  ${CSSDefaultInputField}
  caret-color: ${getThemeVariantColor("context")};
  resize: none;
  padding: 1rem;

  &::placeholder {
    color: ${COLOR_PALETTE.slateGrey60};
    font-weight: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
/**
 * TextArea with Formik Field bindings.
 * Accepts custom + all standard textarea input props.
 * Includes auto-resize functionality to scale textarea to size of parent.
 *
 * Warning:
 * If maxRows isn't set and TextAreaField is a descendent of an AutoSizer component (such as the one inside ScrollBox),
 * the TextAreaField may take up more vertical space than needed. This is likely because the TextAreaField's parent has
 * `width: fill`.
 * You can solve this by wrapping it in Columns/Column combination, which has `width: 100%`
 *
 * @example
 * <TextAreaField name="notes" placeholder={'Make a note...'} />
 */ const TextAreaField = ({ name , label , sublabel , minRows =1 , testId ="X" , throttleMS , reserveMessageSpace =true , onFieldChange , onFieldBlur , ...props })=>{
    const [field, meta] = useField(name);
    const { onBlur , onChange , onFocus , value  } = useTextFieldWrappers(name, onFieldChange, onFieldBlur, throttleMS);
    return React__default.createElement(Stack, {
        "data-hpy-web-textarea-field": true,
        align: "left",
        gap: 0.75
    }, typeof label === "string" ? React__default.createElement(Text, {
        weight: "bold",
        size: "medium"
    }, label) : label, sublabel && React__default.createElement(Text, {
        size: "medium"
    }, sublabel), React__default.createElement(Stack, {
        ...setErrorDataAttribute(hasError(meta)),
        ...setDisabledDataAttribute(!!props.disabled),
        gap: 0.25
    }, React__default.createElement(StyledReactAutosizeTextArea, {
        ...props,
        ...field,
        ...setTestId("TextAreaField-" + testId),
        ...setReadOnlyDataAttribute(!!props.disabled || !!props.readOnly),
        "data-hpy-web-textarea-field-input": true,
        minRows: minRows,
        value: value,
        onChange: onChange,
        onBlur: onBlur,
        onFocus: onFocus
    }), React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace
    })));
};
TextAreaField.dataId = "data-hpy-web-textarea-field";
TextAreaField.displayName = "TextAreaField";

export { TextAreaField };
